import React, { memo } from 'react'
import PropTypes from 'prop-types'
import { Box, Typography, Tooltip, useTheme } from '@mui/material'
import moment from 'moment'
import GridBackground from './GridBackground'
import { useSmartTranslation } from '../../../../generic/hooks/useSmartTranslation'
import { useDateFormatter } from '../../../../generic/hooks/useDateFormatter'

/**
 * Renders a single timeline row with segments.
 * @param {object} props
 * @param {string} props.label - Row label.
 * @param {Array} props.items - List of segments to render.
 * @param {moment.Moment} props.windowStart - Start of the time window.
 * @param {moment.Moment} props.windowEnd - End of the time window.
 */
const TimelineRow = ({ label, items, windowStart, windowEnd }) => {
  const theme = useTheme()
  const windowMs = windowEnd.diff(windowStart)
  const nowMs = moment().valueOf()
  const windowStartMs = windowStart.valueOf()
  const { t_ } = useSmartTranslation()
  const { formatDateTime } = useDateFormatter()
  // Calculate "now" line position
  const nowPct = ((nowMs - windowStartMs) / windowMs) * 100

  const moreThan24Hours = windowMs > 24 * 60 * 60 * 1000

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, height: 40 }}>
      <Typography
        variant='caption'
        sx={{
          width: 120,
          minWidth: 120,
          textAlign: 'right',
          pr: 2,
          color: 'text.secondary',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          fontSize: '0.7rem'
        }}
      >
        {label}
      </Typography>

      {/* Bar container with relative position */}
      <Box sx={{
        flexGrow: 1,
        height: '100%',
        position: 'relative',
        bgcolor: theme.palette.action.hover, // Subtle background for the lane
        borderRadius: 1,
        overflow: 'hidden',
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.1)' // Subtle depth
      }}
      >
        <GridBackground windowStart={windowStart} windowEnd={windowEnd} />

        {/* Render absolute segments */}
        {items && items.map((item, index) => {
          const start = moment(item.start)
          // If end is null or undefined, assume it's active until now
          const end = item.end ? moment(item.end) : moment()

          const segStartMs = Math.max(start.valueOf(), windowStartMs)
          const segEndMs = Math.min(end.valueOf(), windowEnd.valueOf())

          // If segment is outside the window, do not render
          if (segStartMs >= segEndMs) return null

          const leftPct = ((segStartMs - windowStartMs) / windowMs) * 100
          const widthPct = ((segEndMs - segStartMs) / windowMs) * 100

          return (
            <Tooltip
              key={index}
              arrow
              placement='top'
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: 'background.paper',
                    color: 'text.primary',
                    border: `1px solid ${theme.palette.divider}`,
                    boxShadow: theme.shadows[4],
                    p: 0,
                    borderRadius: 1,
                    maxWidth: 300
                  }
                }
              }}
              title={
                <Box sx={{ p: 1.5 }}>
                  <Typography variant='subtitle2' sx={{ fontWeight: 700, mb: 0.5, borderBottom: `1px solid ${theme.palette.divider}`, pb: 0.5 }}>
                    {item.label}
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 1, rowGap: 0.5 }}>
                    <Typography variant='caption' color='text.secondary'>{t_('Start')}:</Typography>
                    <Typography variant='caption' fontWeight='500'>{moreThan24Hours ? formatDateTime(start) : start.format('HH:mm')}</Typography>

                    <Typography variant='caption' color='text.secondary'>{t_('End')}:</Typography>
                    <Typography variant='caption' fontWeight='500'>{item.end ? (moreThan24Hours ? formatDateTime(end) : end.format('HH:mm')) : t_('In Progress')}</Typography>

                    <Typography variant='caption' color='text.secondary'>{t_('Duration')}:</Typography>
                    <Typography variant='caption' fontWeight='500'>{moment.duration(segEndMs - segStartMs).humanize()}</Typography>
                  </Box>
                </Box>
              }
            >
              <Box
                tabIndex={0}
                role='button'
                aria-label={`${item.label} de ${moreThan24Hours ? formatDateTime(start) : start.format('HH:mm')} a ${item.end ? (moreThan24Hours ? formatDateTime(end) : end.format('HH:mm')) : 'ahora'}`}
                sx={{
                  position: 'absolute',
                  left: `${leftPct}%`,
                  width: `${widthPct}%`,
                  top: 5,
                  bottom: 5,
                  bgcolor: item.color,
                  borderRadius: 0.75,
                  display: 'flex',
                  alignItems: 'center',
                  pl: 1,
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  cursor: 'pointer',
                  border: '1px solid rgba(255,255,255,0.1)',
                  boxShadow: 1,
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  zIndex: 1,
                  outline: 'none',
                  '&:hover, &:focus': {
                    zIndex: 10,
                    boxShadow: 4,
                    filter: 'brightness(1.1)',
                    transform: 'scaleY(1.05)'
                  }
                }}
              >
                <Typography
                  variant='caption' sx={{
                    color: theme.palette.getContrastText(item.color || '#000'),
                    fontWeight: 700,
                    fontSize: '0.7rem',
                    textShadow: '0 0 2px rgba(0,0,0,0.3)',
                    letterSpacing: '0.02em'
                  }}
                >
                  {item.label}
                </Typography>
              </Box>
            </Tooltip>
          )
        })}

        {/* "Now" Line */}
        {nowPct >= 0 && nowPct <= 100 && (
          <Box
            sx={{
              position: 'absolute',
              left: `${nowPct}%`,
              top: 0,
              bottom: 0,
              width: '2px',
              bgcolor: theme.palette.error.main,
              zIndex: 20,
              pointerEvents: 'none',
              boxShadow: `0 0 6px ${theme.palette.error.main}`,
              '&::after': {
                content: '""',
                position: 'absolute',
                top: -2,
                left: -3,
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: theme.palette.error.main,
                boxShadow: `0 0 4px ${theme.palette.error.main}`
              }
            }}
          />
        )}
      </Box>
    </Box>
  )
}

TimelineRow.propTypes = {
  label: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(PropTypes.shape({
    start: PropTypes.string.isRequired,
    end: PropTypes.string,
    label: PropTypes.string.isRequired,
    color: PropTypes.string
  })),
  windowStart: PropTypes.object.isRequired,
  windowEnd: PropTypes.object.isRequired
}

export default memo(TimelineRow)
